import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { API } from '../api';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Layout from '../components/Layout';

const BOOKING_PFX = 'BK';

const DEFAULT_SIGN_TEXT = `Note: In order to be covered by CDW or FULL CDW or TPL conditions, client must present a police and INS report.

I Declare that I have read the CDW, FULL CDW, TPL restrictions, which I accept and sign in agreement, also I accept that these conditions were clearly explained.`;

const EMPTY_FORM = {
  contractno: '', bookingrefrence: '',
  headtext: 'Ori E Rental\nA-41, OLD Town, Rameswar Patna, 751002\nEmail : contact@orisysinfotech.com\nMobile-: +91234567899',
  fullname: '', address: '', city: '', online_country: '', zip: '',
  phone: '', passport: '', license: '', datein: '', expdate: '', email: '',
  additionaldriver1: '', passport1: '', license1: '', expdate1: '',
  vehicle1: '', v_color: '', platenumber: '', oilout: '', oilin: '', kmsout: '', kmsin: '',
  online_pickdate: '', online_picktime: '', online_returndate: '', online_returntime: '',
  pickup_location: '', pickup_city: '', returnlo_cation: '', retun_city: '',
  aut1: '', aut2: '', agentout: '', agentin: '',
  diffdate: '', rentalrs: '', onewayfee: 'Oneway Fee', onewaysfeecharge: '',
  pickuplabel: 'Pickup Charge', pickup_charge: '',
  dropofflabel: 'Dropoff Charge', drop_up_charge: '',
  nightchargelabel: 'Night Charge', nightcharge: '',
  optionalcost: 'Optional Cost', optionalcharge: '',
  insurancecharge: '', othercost: 'Other Cost', othercost_charge: '',
  totalrs: '', localtaxpercent: '', localtax: '', cash_discpercent: '', cash_discount: '',
  grandtotalrs: '',
  signtext: DEFAULT_SIGN_TEXT,
};

const RATE_FIELDS = new Set([
  'rentalrs', 'onewaysfeecharge', 'pickup_charge', 'drop_up_charge',
  'nightcharge', 'optionalcharge', 'insurancecharge', 'othercost_charge',
  'localtaxpercent', 'cash_discpercent',
]);

function calcTotals(f) {
  const n = (k) => parseFloat(f[k] || 0);
  const estTotal = n('rentalrs') + n('nightcharge') + n('drop_up_charge') +
    n('pickup_charge') + n('optionalcharge') + n('othercost_charge') +
    n('insurancecharge') + n('onewaysfeecharge');
  const taxPct   = parseFloat(f.localtaxpercent || 0);
  const localtax = estTotal * taxPct / 100;
  const discPct  = parseFloat(f.cash_discpercent || 0);
  const discAmt  = discPct ? (estTotal + localtax) * discPct / 100 : parseFloat(f.cash_discount || 0);
  const grand    = estTotal + localtax - discAmt;
  return {
    totalrs:       estTotal.toFixed(2),
    localtax:      localtax.toFixed(2),
    cash_discount: discAmt.toFixed(2),
    grandtotalrs:  grand.toFixed(2),
  };
}

// ─── CRITICAL: Inp and RateInp are defined OUTSIDE the component ──────────────
// Defining them inside causes React to see a NEW component type on every render,
// which unmounts+remounts the input DOM node → cursor/focus lost after each keystroke.
// Passing value+onChange as props keeps them as stable component references.

const Inp = ({ name, label, type, placeholder, readOnly, full, value, onChange }) => (
  <div style={{ ...S.fg, gridColumn: full ? '1 / -1' : undefined }}>
    {label && <label style={S.label}>{label}</label>}
    <input
      type={type || 'text'}
      name={name}
      value={value ?? ''}
      onChange={onChange}
      placeholder={placeholder || label || ''}
      readOnly={readOnly}
      style={{ ...S.input, background: readOnly ? '#f8f8f8' : '#fff' }}
    />
  </div>
);

const RateInp = ({ name, labelName, labelDefault, formValues, onChange }) => (
  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 8 }}>
    <input
      type="text"
      name={labelName}
      value={formValues[labelName] ?? labelDefault ?? ''}
      onChange={onChange}
      placeholder="Label"
      style={{ ...S.input, fontSize: 12 }}
    />
    <input
      type="text"
      name={name}
      value={formValues[name] ?? ''}
      onChange={onChange}
      placeholder="0.00"
      style={{ ...S.input, textAlign: 'right' }}
    />
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────

export default function ManageContract() {
  const navigate = useNavigate();

  const [data,       setData]       = useState([]);
  const [page,       setPage]       = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total,      setTotal]      = useState(0);
  const [search,     setSearch]     = useState('');
  const [loading,    setLoading]    = useState(false);
  const [selected,   setSelected]   = useState([]);

  const [formOpen,   setFormOpen]   = useState(false);
  const [form,       setForm]       = useState(EMPTY_FORM);
  const [editId,     setEditId]     = useState(null);
  const [saving,     setSaving]     = useState(false);
  const [bookingId,  setBookingId]  = useState(null);

  const [viewData,   setViewData]   = useState(null);

  useEffect(() => { if (!localStorage.getItem('token')) navigate('/login'); }, []);

  const fetchData = useCallback((pg, q) => {
    setLoading(true); setSelected([]);
    API.get('/contracts', { params: { page: pg, limit: 10, search: q } })
      .then(r => { setData(r.data.data); setTotalPages(r.data.totalPages); setTotal(r.data.total); })
      .catch(() => toast.error('Failed to load contracts'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { fetchData(page, search); }, [page]);

  useEffect(() => {
    const t = setTimeout(() => { setPage(1); fetchData(1, search); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Single unified onChange — one setForm call, no setTimeout, no stale closures
  const handleChange = useCallback((e) => {
    const { name, value } = e.target;
    setForm(prev => {
      const next = { ...prev, [name]: value };
      return RATE_FIELDS.has(name) ? { ...next, ...calcTotals(next) } : next;
    });
  }, []);

  const openAdd = async (bkId = null) => {
    try {
      const metaRes  = await API.get('/contracts/meta/nextid');
      const nextId   = metaRes.data.nextId;
      const contractno      = `${BOOKING_PFX}-REG${String(nextId).padStart(6, '0')}`;
      const bookingrefrence = `${BOOKING_PFX}-BR${String(nextId).padStart(6, '0')}`;

      if (bkId) {
        const r  = await API.get(`/contracts/prefill/${bkId}`);
        const bk = r.data;
        const rentalFee  = parseFloat(bk.online_onlineprice || 0) * parseInt(bk.online_diffdate || 1);
        const insCharge  = bk.ins_total   || 0;
        const optCharge  = bk.opt_total   || 0;
        const pickCharge = bk.online_pickcharge || 0;
        const dropCharge = bk.online_dropcharge || 0;
        const nightCh    = (bk.online_picknightcharge || 0) + (bk.online_dropoffnightcharge || 0);
        const onewayFee  = bk.oneway_rent_chrg || 0;
        const estTotal   = rentalFee + nightCh + pickCharge + dropCharge + optCharge + insCharge + onewayFee;
        const taxAmt     = estTotal * (bk.tax_rate / 100);
        const grandTotal = estTotal + taxAmt - (bk.cash_discamt || 0);

        setForm({
          ...EMPTY_FORM, contractno, bookingrefrence,
          fullname:          `${bk.online_title || ''} ${bk.online_firstname || ''} ${bk.online_lastname || ''}`.trim(),
          address:           bk.online_address    || '',
          city:              bk.online_city        || '',
          online_country:    bk.online_country     || '',
          zip:               bk.online_postcode    || '',
          phone:             bk.online_mobileno    || '',
          license:           bk.dl_number          || '',
          expdate:           bk.dl_expirydate?.slice(0, 10) || '',
          email:             bk.online_emailid     || '',
          vehicle1:          bk.car_name           || '',
          online_pickdate:   bk.online_pickdate?.slice(0, 10)   || '',
          online_picktime:   bk.online_picktime    || '',
          online_returndate: bk.online_returndate?.slice(0, 10) || '',
          online_returntime: bk.online_returntime  || '',
          pickup_location:   bk.pick_station_name  || '',
          pickup_city:       bk.pick_city_name     || '',
          returnlo_cation:   bk.return_station_name || '',
          retun_city:        bk.return_city_name   || '',
          diffdate:          bk.online_diffdate    || '',
          rentalrs:          rentalFee.toFixed(2),
          nightcharge:       nightCh.toFixed(2),
          pickup_charge:     pickCharge.toFixed(2),
          drop_up_charge:    dropCharge.toFixed(2),
          optionalcharge:    optCharge.toFixed(2),
          insurancecharge:   insCharge.toFixed(2),
          onewaysfeecharge:  onewayFee.toFixed(2),
          totalrs:           estTotal.toFixed(2),
          localtaxpercent:   bk.tax_rate,
          localtax:          taxAmt.toFixed(2),
          cash_discpercent:  bk.cash_discpercent || 0,
          cash_discount:     (bk.cash_discamt || 0).toFixed(2),
          grandtotalrs:      grandTotal.toFixed(2),
        });
        setBookingId(bkId);
      } else {
        setForm({ ...EMPTY_FORM, contractno, bookingrefrence });
        setBookingId(null);
      }
      setEditId(null);
      setFormOpen(true);
    } catch { toast.error('Failed to prepare form'); }
  };

  const openEdit = async (id) => {
    try {
      const r = await API.get(`/contracts/${id}`);
      setForm({ ...EMPTY_FORM, ...r.data.contract_data });
      setEditId(id);
      setBookingId(r.data.booking_id);
      setFormOpen(true);
    } catch { toast.error('Failed to load contract'); }
  };

  const openView = async (id) => {
    try {
      const r = await API.get(`/contracts/${id}`);
      setViewData(r.data);
    } catch { toast.error('Failed to load contract'); }
  };

  const handleSubmit = async () => {
    if (!form.fullname?.trim()) { toast.warning('Driver name is required'); return; }
    setSaving(true);
    try {
      if (editId) {
        await API.put(`/contracts/${editId}`, { contract_data: form });
        toast.success('Contract updated');
      } else {
        await API.post('/contracts', { booking_id: bookingId, contract_data: form });
        toast.success('Contract created');
      }
      setFormOpen(false);
      fetchData(page, search);
    } catch (err) { toast.error(err.response?.data?.error || 'Operation failed'); }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this contract?')) return;
    try { await API.delete(`/contracts/${id}`); toast.success('Deleted'); fetchData(page, search); }
    catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const handleBulkDelete = async () => {
    if (!selected.length) { toast.warning('Select contracts first'); return; }
    if (!window.confirm(`Delete ${selected.length} contract(s)?`)) return;
    try { await API.delete('/contracts/bulk/delete', { data: { ids: selected } }); toast.success('Deleted'); fetchData(page, search); }
    catch (err) { toast.error(err.response?.data?.error || 'Delete failed'); }
  };

  const toggleAll = () => setSelected(selected.length === data.length ? [] : data.map(r => r.conract_id));
  const toggleOne = (id) => setSelected(p => p.includes(id) ? p.filter(x => x !== id) : [...p, id]);
  const fmtDate   = d => d ? new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' }) : '—';

  return (
    <Layout activePage="Manage Rent Agreement">
      <ToastContainer position="top-right" autoClose={3000} />

      <div style={S.header}>
        <div>
          <h2 style={S.title}>Manage Rent Agreement</h2>
          <p style={S.subtitle}>{total} agreement{total !== 1 ? 's' : ''}</p>
        </div>
        <button style={S.addBtn} onClick={() => openAdd()}>➕ Add Agreement</button>
      </div>

      <div style={S.topRow}>
        <div style={S.searchWrap}>
          <span style={{ fontSize: 13, color: '#aaa' }}>🔍</span>
          <input type="text" placeholder="Search by Agreement No / Booking Ref / Name..."
            value={search} onChange={e => setSearch(e.target.value)} style={S.searchInput} />
          {search && <button style={S.clearBtn} onClick={() => setSearch('')}>✕</button>}
        </div>
        <button style={S.deleteBtn} disabled={!selected.length} onClick={handleBulkDelete}>
          🗑️ Delete Selected
        </button>
      </div>

      <div style={S.card}>
        <div style={{ overflowX: 'auto' }}>
          <table style={S.table}>
            <thead>
              <tr style={S.theadRow}>
                <th style={{ ...S.th, width: 36 }}>
                  <input type="checkbox" checked={data.length > 0 && selected.length === data.length}
                    onChange={toggleAll} style={{ accentColor: '#6C63FF' }} />
                </th>
                <th style={S.th}>Agreement No</th>
                <th style={S.th}>Booking Ref No</th>
                <th style={S.th}>Customer Name</th>
                <th style={S.th}>Last Updated</th>
                <th style={{ ...S.th, textAlign: 'right' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={6} style={S.emptyCell}>Loading…</td></tr>
              ) : data.length === 0 ? (
                <tr><td colSpan={6} style={S.emptyCell}>No records found</td></tr>
              ) : data.map(row => {
                const isSel = selected.includes(row.conract_id);
                return (
                  <tr key={row.conract_id} style={{ ...S.tr, background: isSel ? '#f3f2ff' : 'transparent' }}>
                    <td style={S.td}>
                      <input type="checkbox" checked={isSel} onChange={() => toggleOne(row.conract_id)} style={{ accentColor: '#6C63FF' }} />
                    </td>
                    <td style={S.td}><span style={S.refChip}>{row.contractno || '—'}</span></td>
                    <td style={S.td}><span style={S.bkChip}>{row.bookingrefrence || '—'}</span></td>
                    <td style={S.td}><span style={{ fontWeight: 600, fontSize: 13 }}>{row.fullname || '—'}</span></td>
                    <td style={{ ...S.td, fontSize: 12, color: '#777' }}>{fmtDate(row.updatedate)}</td>
                    <td style={{ ...S.td, textAlign: 'right' }}>
                      <button style={S.viewBtn} onClick={() => openView(row.conract_id)}>View / Print</button>
                      <button style={S.editBtn} onClick={() => openEdit(row.conract_id)}>Update</button>
                      <button style={S.delBtn}  onClick={() => handleDelete(row.conract_id)}>Delete</button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div style={S.pagination}>
          <span style={S.paginInfo}>{total > 0 ? `Showing ${(page-1)*10+1}–${Math.min(page*10,total)} of ${total}` : ''}</span>
          <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
            <button style={S.pgBtn} disabled={page===1} onClick={() => setPage(p=>p-1)}>← Prev</button>
            <span style={S.paginInfo}>Page {page} / {totalPages||1}</span>
            <button style={S.pgBtn} disabled={page===totalPages||totalPages===0} onClick={() => setPage(p=>p+1)}>Next →</button>
          </div>
        </div>
      </div>

      {/* ── Form Modal ── */}
      {formOpen && (
        <div style={S.overlay} onClick={() => !saving && setFormOpen(false)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                {editId ? '✏️ Update Rent Agreement' : '➕ Create Rent Agreement'}
              </h3>
              <button style={S.closeBtn} onClick={() => !saving && setFormOpen(false)}>✕</button>
            </div>
            <div style={S.modalBody}>

              <div style={S.section}>Agreement Header</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <Inp name="contractno"      label="Agreement No"   readOnly value={form.contractno}      onChange={handleChange} />
                <Inp name="bookingrefrence" label="Booking Ref No" readOnly value={form.bookingrefrence} onChange={handleChange} />
              </div>
              <div style={S.fg}>
                <label style={S.label}>Company Header Text</label>
                <textarea name="headtext" value={form.headtext ?? ''} onChange={handleChange} rows={3}
                  style={{ ...S.input, resize: 'vertical', fontFamily: 'inherit' }} />
              </div>

              <div style={S.section}>Driver Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <Inp name="fullname"       label="Driver Name *"   value={form.fullname}       onChange={handleChange} />
                <Inp name="address"        label="Address"         value={form.address}        onChange={handleChange} />
                <Inp name="city"           label="City"            value={form.city}           onChange={handleChange} />
                <Inp name="online_country" label="State / Country" value={form.online_country} onChange={handleChange} />
                <Inp name="zip"            label="Zip Code"        value={form.zip}            onChange={handleChange} />
                <Inp name="phone"          label="Phone Number"    value={form.phone}          onChange={handleChange} />
                <Inp name="passport"       label="Passport Number" value={form.passport}       onChange={handleChange} />
                <Inp name="license"        label="License Number"  value={form.license}        onChange={handleChange} />
                <Inp name="datein"  type="date"  label="Date In"         value={form.datein}  onChange={handleChange} />
                <Inp name="expdate" type="date"  label="Expiration Date" value={form.expdate} onChange={handleChange} />
                <Inp name="email"   type="email" label="Email Address"   value={form.email}   onChange={handleChange} full />
              </div>
              <div style={S.sectionSub}>Additional Driver</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <Inp name="additionaldriver1" label="Additional Driver" value={form.additionaldriver1} onChange={handleChange} />
                <Inp name="passport1"         label="Passport No"      value={form.passport1}         onChange={handleChange} />
                <Inp name="license1"          label="License No"       value={form.license1}          onChange={handleChange} />
                <Inp name="expdate1" type="date" label="Expiry Date"   value={form.expdate1}          onChange={handleChange} />
              </div>

              <div style={S.section}>Vehicle Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <Inp name="vehicle1"    label="Vehicle Model" value={form.vehicle1}    onChange={handleChange} />
                <Inp name="v_color"     label="Vehicle Color" value={form.v_color}     onChange={handleChange} />
                <Inp name="platenumber" label="Plate Number"  value={form.platenumber} onChange={handleChange} />
                <Inp name="oilout"      label="Oil Out"       value={form.oilout}      onChange={handleChange} />
                <Inp name="oilin"       label="Oil In"        value={form.oilin}       onChange={handleChange} />
                <Inp name="kmsout"      label="KMs Out"       value={form.kmsout}      onChange={handleChange} />
                <Inp name="kmsin"       label="KMs In"        value={form.kmsin}       onChange={handleChange} />
              </div>

              <div style={S.section}>Journey Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div style={S.fg}>
                  <label style={S.label}>Pick-up Date & Time</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input type="date" name="online_pickdate"  value={form.online_pickdate  ?? ''} onChange={handleChange} style={{ ...S.input, flex: 1 }} />
                    <input type="time" name="online_picktime"  value={form.online_picktime  ?? ''} onChange={handleChange} style={{ ...S.input, flex: 1 }} />
                  </div>
                </div>
                <div style={S.fg}>
                  <label style={S.label}>Return Date & Time</label>
                  <div style={{ display: 'flex', gap: 6 }}>
                    <input type="date" name="online_returndate" value={form.online_returndate ?? ''} onChange={handleChange} style={{ ...S.input, flex: 1 }} />
                    <input type="time" name="online_returntime" value={form.online_returntime ?? ''} onChange={handleChange} style={{ ...S.input, flex: 1 }} />
                  </div>
                </div>
                <Inp name="pickup_location"  label="Pickup Location"  value={form.pickup_location}  onChange={handleChange} />
                <Inp name="pickup_city"      label="Pickup City"      value={form.pickup_city}      onChange={handleChange} />
                <Inp name="returnlo_cation"  label="Return Location"  value={form.returnlo_cation}  onChange={handleChange} />
                <Inp name="retun_city"       label="Return City"      value={form.retun_city}       onChange={handleChange} />
              </div>

              <div style={S.section}>Additional Details</div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr', gap: 12, marginBottom: 14 }}>
                <Inp name="aut1"     label="AUT.1"     value={form.aut1}     onChange={handleChange} />
                <Inp name="aut2"     label="AUT.2"     value={form.aut2}     onChange={handleChange} />
                <Inp name="agentout" label="Agent Out" value={form.agentout} onChange={handleChange} />
                <Inp name="agentin"  label="Agent In"  value={form.agentin}  onChange={handleChange} />
              </div>

              <div style={S.section}>Rate Chart</div>
              <div style={{ background: '#fafafa', borderRadius: 12, padding: 16, marginBottom: 14 }}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 10 }}>
                  Rental duration: <strong>{form.diffdate || '—'} days</strong>
                </div>
                <RateInp name="rentalrs"         labelName="_rl"              labelDefault="Rental Fee"     formValues={form} onChange={handleChange} />
                <RateInp name="onewaysfeecharge" labelName="onewayfee"        labelDefault="Oneway Fee"     formValues={form} onChange={handleChange} />
                <RateInp name="pickup_charge"    labelName="pickuplabel"      labelDefault="Pickup Charge"  formValues={form} onChange={handleChange} />
                <RateInp name="drop_up_charge"   labelName="dropofflabel"     labelDefault="Dropoff Charge" formValues={form} onChange={handleChange} />
                <RateInp name="nightcharge"      labelName="nightchargelabel" labelDefault="Night Charge"   formValues={form} onChange={handleChange} />
                <RateInp name="optionalcharge"   labelName="optionalcost"     labelDefault="Optional Cost"  formValues={form} onChange={handleChange} />
                <RateInp name="insurancecharge"  labelName="_il"              labelDefault="Insurance Cost" formValues={form} onChange={handleChange} />
                <RateInp name="othercost_charge" labelName="othercost"        labelDefault="Other Cost"     formValues={form} onChange={handleChange} />

                <div style={S.summaryDivider} />

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#555', alignSelf: 'center' }}>Tax %</div>
                  <input type="text" name="localtaxpercent" value={form.localtaxpercent ?? ''}
                    onChange={handleChange} placeholder="0" style={{ ...S.input, textAlign: 'right' }} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                  <div style={{ fontSize: 13, fontWeight: 500, color: '#555', alignSelf: 'center' }}>Discount %</div>
                  <input type="text" name="cash_discpercent" value={form.cash_discpercent ?? ''}
                    onChange={handleChange} placeholder="0" style={{ ...S.input, textAlign: 'right' }} />
                </div>

                <div style={S.summaryDivider} />

                {[
                  ['Estimated Total', 'totalrs',      true,  false],
                  ['Local Tax',       'localtax',      true,  false],
                  ['Discount',        'cash_discount', true,  false],
                  ['Total Amount',    'grandtotalrs',  true,  true ],
                ].map(([lbl, key, readOnly, bold]) => (
                  <div key={key} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 6 }}>
                    <div style={{ fontSize: 13, fontWeight: bold ? 700 : 500, color: bold ? '#1a1d2e' : '#555', alignSelf: 'center' }}>{lbl}</div>
                    <input type="text" name={key} value={form[key] ?? ''} readOnly={readOnly}
                      style={{ ...S.input, textAlign: 'right', background: readOnly ? '#f3f2ff' : '#fff',
                        fontWeight: bold ? 800 : 400, fontSize: bold ? 15 : 13, color: '#c0392b' }} />
                  </div>
                ))}
              </div>

              <div style={S.section}>Signature Block</div>
              <div style={S.fg}>
                <label style={S.label}>Terms / Signature Text</label>
                <textarea name="signtext" value={form.signtext ?? ''} onChange={handleChange} rows={5}
                  style={{ ...S.input, resize: 'vertical', fontFamily: 'inherit', fontSize: 12 }} />
              </div>

            </div>
            <div style={S.modalFoot}>
              <button style={S.cancelBtn} onClick={() => !saving && setFormOpen(false)}>Cancel</button>
              <button style={{ ...S.submitBtn, opacity: saving ? 0.7 : 1 }} onClick={handleSubmit} disabled={saving}>
                {saving ? 'Saving…' : editId ? 'Update Agreement' : 'Create Agreement'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── View / Print Modal ── */}
      {viewData && (
        <div style={S.overlay} onClick={() => setViewData(null)}>
          <div style={{ ...S.modalBox, maxWidth: 860 }} onClick={e => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                📄 {viewData.contract_data?.contractno || 'Rent Agreement'}
              </h3>
              <div style={{ display: 'flex', gap: 8 }}>
                <button style={S.printBtn} onClick={() => window.print()}>🖨️ Print</button>
                <button style={S.closeBtn} onClick={() => setViewData(null)}>✕</button>
              </div>
            </div>
            <div style={{ ...S.modalBody, fontFamily: 'Arial, sans-serif' }} id="printArea">
              {(() => {
                const d = viewData.contract_data || {};
                const ViewRow = ({ label, value }) => !value ? null : (
                  <tr>
                    <td style={{ padding: '6px 10px', fontSize: 12, color: '#888', width: '35%', fontWeight: 600 }}>{label}</td>
                    <td style={{ padding: '6px 10px', fontSize: 13, color: '#222' }}>{value}</td>
                  </tr>
                );
                return (
                  <>
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 20, borderBottom: '2px solid #fdcc6d', paddingBottom: 12 }}>
                      <pre style={{ fontSize: 13, margin: 0, fontFamily: 'Arial' }}>{d.headtext}</pre>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ fontSize: 12, color: '#888' }}>Agreement No</div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{d.contractno}</div>
                        <div style={{ fontSize: 12, color: '#888', marginTop: 6 }}>Booking Ref No</div>
                        <div style={{ fontSize: 14, fontWeight: 700 }}>{d.bookingrefrence}</div>
                      </div>
                    </div>
                    <div style={{ background: '#fdcc6d', padding: '6px 12px', fontWeight: 700, fontSize: 14, marginBottom: 8 }}>Driver Details</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
                      <ViewRow label="Driver Name"    value={d.fullname} />
                      <ViewRow label="Address"        value={d.address} />
                      <ViewRow label="City"           value={d.city} />
                      <ViewRow label="Country"        value={d.online_country} />
                      <ViewRow label="Zip"            value={d.zip} />
                      <ViewRow label="Phone"          value={d.phone} />
                      <ViewRow label="Email"          value={d.email} />
                      <ViewRow label="Passport"       value={d.passport} />
                      <ViewRow label="License No"     value={d.license} />
                      <ViewRow label="License Expiry" value={d.expdate} />
                      {d.additionaldriver1 && <ViewRow label="Additional Driver" value={d.additionaldriver1} />}
                    </tbody></table>
                    <div style={{ background: '#fdcc6d', padding: '6px 12px', fontWeight: 700, fontSize: 14, margin: '12px 0 8px' }}>Vehicle Details</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
                      <ViewRow label="Vehicle Model" value={d.vehicle1} />
                      <ViewRow label="Color"         value={d.v_color} />
                      <ViewRow label="Plate Number"  value={d.platenumber} />
                      <ViewRow label="Oil Out / In"  value={d.oilout && d.oilin ? `${d.oilout} / ${d.oilin}` : null} />
                      <ViewRow label="KMs Out / In"  value={d.kmsout && d.kmsin ? `${d.kmsout} / ${d.kmsin}` : null} />
                    </tbody></table>
                    <div style={{ background: '#fdcc6d', padding: '6px 12px', fontWeight: 700, fontSize: 14, margin: '12px 0 8px' }}>Journey Details</div>
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}><tbody>
                      <ViewRow label="Pick-up"          value={d.online_pickdate   && `${d.online_pickdate} ${d.online_picktime || ''}`} />
                      <ViewRow label="Return"           value={d.online_returndate && `${d.online_returndate} ${d.online_returntime || ''}`} />
                      <ViewRow label="Pick-up Location" value={`${d.pickup_location || ''} ${d.pickup_city ? '— '+d.pickup_city : ''}`} />
                      <ViewRow label="Return Location"  value={`${d.returnlo_cation || ''} ${d.retun_city ? '— '+d.retun_city : ''}`} />
                    </tbody></table>
                    <div style={{ background: '#fdcc6d', padding: '6px 12px', fontWeight: 700, fontSize: 14, margin: '12px 0 8px' }}>Rate Chart</div>
                    <table style={{ width: '50%', borderCollapse: 'collapse', marginLeft: 'auto' }}><tbody>
                      {[['Rental Fee',d.rentalrs],['Oneway Fee',d.onewaysfeecharge],['Pickup Charge',d.pickup_charge],
                        ['Dropoff Charge',d.drop_up_charge],['Night Charge',d.nightcharge],['Optional Cost',d.optionalcharge],
                        ['Insurance Cost',d.insurancecharge],['Other Cost',d.othercost_charge]]
                        .filter(([,v]) => v && parseFloat(v) > 0).map(([l,v]) => (
                          <tr key={l}>
                            <td style={{ padding: '4px 10px', fontSize: 12, color: '#555' }}>{l}</td>
                            <td style={{ padding: '4px 10px', fontSize: 13, textAlign: 'right', color: '#c0392b', fontWeight: 600 }}>{parseFloat(v).toFixed(2)}</td>
                          </tr>
                        ))}
                      <tr style={{ borderTop: '1px solid #ddd' }}>
                        <td style={{ padding: '6px 10px', fontSize: 13, fontWeight: 700 }}>Estimated Total</td>
                        <td style={{ padding: '6px 10px', fontSize: 14, textAlign: 'right', color: '#c0392b', fontWeight: 800 }}>{d.totalrs}</td>
                      </tr>
                      {parseFloat(d.localtax) > 0 && <tr><td style={{ padding: '4px 10px', fontSize: 12 }}>Local Tax</td><td style={{ padding: '4px 10px', textAlign: 'right', color: '#c0392b' }}>{d.localtax}</td></tr>}
                      {parseFloat(d.cash_discount) > 0 && <tr><td style={{ padding: '4px 10px', fontSize: 12 }}>Discount</td><td style={{ padding: '4px 10px', textAlign: 'right', color: '#1a9e5c' }}>-{d.cash_discount}</td></tr>}
                      <tr style={{ borderTop: '2px solid #fdcc6d' }}>
                        <td style={{ padding: '8px 10px', fontSize: 14, fontWeight: 800 }}>Total Amount</td>
                        <td style={{ padding: '8px 10px', fontSize: 16, textAlign: 'right', color: '#c0392b', fontWeight: 800 }}>{d.grandtotalrs}</td>
                      </tr>
                    </tbody></table>
                    <div style={{ border: '1px solid #ddd', borderRadius: 10, padding: 16, marginTop: 20, fontSize: 12, lineHeight: 1.8, whiteSpace: 'pre-wrap' }}>
                      {d.signtext}
                      <div style={{ marginTop: 40, borderTop: '1px solid #bbb', paddingTop: 10, textAlign: 'center', color: '#777' }}>Customer's Signature</div>
                    </div>
                  </>
                );
              })()}
            </div>
            <div style={S.modalFoot}>
              <button style={S.submitBtn} onClick={() => setViewData(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const S = {
  header:      { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 },
  title:       { margin: 0, fontSize: 24, fontWeight: 800, color: '#1a1d2e', letterSpacing: '-0.5px' },
  subtitle:    { margin: '4px 0 0', fontSize: 13, color: '#999' },
  addBtn:      { padding: '10px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 14, fontWeight: 700, fontFamily: 'inherit', boxShadow: '0 4px 14px rgba(108,99,255,0.35)' },
  topRow:      { display: 'flex', gap: 12, marginBottom: 16, alignItems: 'center', flexWrap: 'wrap' },
  searchWrap:  { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #e8e8e8', borderRadius: 10, padding: '9px 16px', flex: 1, minWidth: 280, boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
  searchInput: { border: 'none', outline: 'none', fontSize: 13, flex: 1, fontFamily: 'inherit', color: '#333' },
  clearBtn:    { background: 'none', border: 'none', cursor: 'pointer', color: '#aaa', fontSize: 12, padding: 0 },
  deleteBtn:   { padding: '9px 18px', background: '#ff4444', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  card:        { background: '#fff', borderRadius: 16, boxShadow: '0 2px 16px rgba(0,0,0,0.06)', overflow: 'hidden' },
  table:       { width: '100%', borderCollapse: 'collapse' },
  theadRow:    { background: '#fafafa' },
  th:          { padding: '12px 14px', fontSize: 11, fontWeight: 700, color: '#999', textAlign: 'left', borderBottom: '2px solid #f0f0f0', textTransform: 'uppercase', letterSpacing: '0.5px', whiteSpace: 'nowrap' },
  tr:          { transition: 'background 0.12s' },
  td:          { padding: '13px 14px', fontSize: 13, color: '#333', borderBottom: '1px solid #f8f8f8' },
  emptyCell:   { textAlign: 'center', padding: 40, color: '#bbb', fontSize: 14 },
  refChip:     { background: '#f3f2ff', color: '#6C63FF', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 700 },
  bkChip:      { background: '#f0f8ff', color: '#1a6eb5', borderRadius: 20, padding: '3px 10px', fontSize: 12, fontWeight: 600 },
  viewBtn:     { marginRight: 5, background: '#f0fff6', color: '#1a9e5c', border: '1px solid #b8f0d0', padding: '4px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  editBtn:     { marginRight: 5, background: '#f3f2ff', color: '#6C63FF', border: '1px solid #d4d0ff', padding: '4px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  delBtn:      { background: '#fff2f5', color: '#FF6584', border: '1px solid #ffd0d8', padding: '4px 12px', borderRadius: 20, cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: 'inherit' },
  pagination:  { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderTop: '1px solid #f0f0f0' },
  paginInfo:   { fontSize: 12, color: '#999' },
  pgBtn:       { padding: '5px 14px', background: '#f3f2ff', color: '#6C63FF', border: '1px solid #d4d0ff', borderRadius: 8, cursor: 'pointer', fontSize: 12, fontWeight: 600, fontFamily: 'inherit' },
  overlay:     { position: 'fixed', inset: 0, background: 'rgba(10,10,30,0.6)', zIndex: 999, display: 'flex', alignItems: 'center', justifyContent: 'center', backdropFilter: 'blur(2px)' },
  modalBox:    { background: '#fff', borderRadius: 20, width: 780, maxWidth: '96vw', maxHeight: '92vh', display: 'flex', flexDirection: 'column', boxShadow: '0 24px 60px rgba(0,0,0,0.25)' },
  modalHead:   { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 22px', borderBottom: '1px solid #f0f0f0', flexShrink: 0 },
  modalBody:   { padding: '20px 22px', overflowY: 'auto', flex: 1 },
  modalFoot:   { display: 'flex', justifyContent: 'flex-end', gap: 10, padding: '14px 22px', borderTop: '1px solid #f0f0f0', flexShrink: 0 },
  closeBtn:    { background: '#ff6b6b22', border: 'none', color: '#ff6b6b', borderRadius: '50%', width: 30, height: 30, cursor: 'pointer', fontWeight: 800, fontSize: 14 },
  printBtn:    { padding: '6px 16px', background: '#fdcc6d', color: '#413232', border: 'none', borderRadius: 8, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  cancelBtn:   { padding: '9px 20px', background: '#f5f5f5', color: '#666', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 600, fontFamily: 'inherit' },
  submitBtn:   { padding: '9px 22px', background: 'linear-gradient(135deg,#6C63FF,#43BCCD)', color: '#fff', border: 'none', borderRadius: 10, cursor: 'pointer', fontSize: 13, fontWeight: 700, fontFamily: 'inherit' },
  section:        { fontSize: 11, fontWeight: 800, color: '#6C63FF', textTransform: 'uppercase', letterSpacing: '0.7px', borderBottom: '1.5px solid #f3f2ff', paddingBottom: 6, marginBottom: 12, marginTop: 20 },
  sectionSub:     { fontSize: 12, fontWeight: 700, color: '#888', marginBottom: 8, marginTop: 12 },
  fg:             { display: 'flex', flexDirection: 'column', gap: 5 },
  label:          { fontSize: 12, fontWeight: 600, color: '#555' },
  input:          { padding: '8px 12px', border: '1.5px solid #e0e0e0', borderRadius: 10, fontSize: 13, fontFamily: 'inherit', color: '#333', outline: 'none', width: '100%', boxSizing: 'border-box' },
  summaryDivider: { borderTop: '1px solid #e0e0e0', margin: '12px 0' },
};